import { Observable } from 'rxjs';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { ROUTER_LOCATION_CHANGED } from '../redux/route';
import { CARD_PAGE } from '../../routes';
import { packagesRequest } from '../redux/packages';
import { hasDifferentRoute } from '../utils/urls/urlComparer';
import {
  CARDS_KEY,
  FLYERS_KEY,
  getActiveTabKey,
  navNodeHasCrossSelling,
} from '../utils/crossSellingTabUtils';

import type { ActionObs, Store, Obs } from '../types';

export default function packageRoute(action$: ActionObs, store: Store): Obs {
  return action$
    .ofType(ROUTER_LOCATION_CHANGED)
    .filter(action => {
      let newRouteInfo = get(action, 'payload');
      // Determine if this is a paper browser cards page route...
      if (
        newRouteInfo.pathType === CARD_PAGE &&
        newRouteInfo.pathname !== '/card'
      ) {
        const {
          route: { currentRoute, previousRoute },
          env: { isMobile },
          packages: {
            currentPage,
            navNode: {
              id: packagesId,
              desktopDefaultCrossSellingTab,
              desktopFlyerCategories,
              mobileDefaultCrossSellingTab,
              mobileFlyerCategories,
            },
          },
        } = store.getState();

        // If we don't have package data (during SSR load...) then load data...
        if (!packagesId) {
          return true;
        }
        // If slug has changed, then we need to reload.
        if (currentRoute.slug !== previousRoute.slug) {
          return true;
        }

        const flyerCategories = isMobile
          ? mobileFlyerCategories
          : desktopFlyerCategories;

        const currentTab = getActiveTabKey(
          currentRoute.query.tab,
          isMobile,
          mobileDefaultCrossSellingTab,
          desktopDefaultCrossSellingTab,
        );

        const previousTab = getActiveTabKey(
          get(previousRoute, 'query.tab'),
          isMobile,
          mobileDefaultCrossSellingTab,
          desktopDefaultCrossSellingTab,
        );

        // If this is a tabbed page and flyers is selected, then don't do anything...
        if (currentTab === FLYERS_KEY && flyerCategories.length) {
          return false;
        }
        // If we changed tabs to the CARDS tab and we don't have page 1's information, then load it...
        if (
          currentTab === CARDS_KEY &&
          currentTab !== previousTab &&
          currentPage !== 1
        ) {
          return true;
        }

        const propsToIgnore = [
          'tab',
          'slug',
          'card',
          'variation_id',
          'suite_variation_id',
        ];

        // Determine if the route has changed significantly...
        return hasDifferentRoute(currentRoute, previousRoute, propsToIgnore);
      }

      return false;
    })
    .map(({ payload }) => {
      const { slug, pathname, query } = payload;
      const state = store.getState();
      const {
        route: { currentRoute },
        env: { isMobile },
        packages: {
          navNode: {
            desktopDefaultCrossSellingTab,
            desktopFlyerCategories,
            mobileDefaultCrossSellingTab,
            mobileFlyerCategories,
          },
        },
      } = state;
      const params = {
        slug: slug || currentRoute.slug,
        category: slug,
        type: pathname.replace('/', ''),
        perPage: isMobile ? 28 : 96,
      };

      let { tab, page = 1 } = query;
      const hasCrossSelling = navNodeHasCrossSelling(
        mobileFlyerCategories,
        desktopFlyerCategories,
        isMobile,
      );
      if (hasCrossSelling) {
        tab = getActiveTabKey(
          tab,
          isMobile,
          mobileDefaultCrossSellingTab,
          desktopDefaultCrossSellingTab,
        );
      }
      page = hasCrossSelling && tab !== CARDS_KEY ? 1 : parseInt(page);

      return { params, query: { ...query, page, tab } };
    })
    .switchMap(({ params, query }) => {
      return Observable.of(packagesRequest({ ...params, query }));
    });
}
